import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { showSuccessToast } from '@/src/renderer/components/common/SuccessToast'
import type { Task } from '@/src/renderer/lib/db/models'
import { db } from '@/src/renderer/lib/db/indexeddb'
import { scheduleSync } from '@/src/renderer/lib/sync/sync-engine'
import { useDoneSound } from '@/src/renderer/lib/sounds'
import { buildTaskUpdate, type StagedAction } from '../../../utils/buildTaskUpdate'
import { useClarifyState } from './useClarifyState'

export interface UseClarifyControllerProps {
  tasks: Task[]
  onExit: () => void
}

/**
 * Orchestrator hook for the Clarify-Mode. Owns the state snapshot, the
 * keyboard shortcuts, and the commit-on-finish transaction. Exposes split
 * props for the header (progress/count/Fertig) and body (list/panel) so the
 * host page can place them in separate layout slots.
 */
export function useClarifyController({ tasks, onExit }: UseClarifyControllerProps) {
  const state = useClarifyState(tasks)
  const {
    taskQueue,
    staged,
    stackIds,
    activeTaskId,
    activeTask,
    progress,
    stageAction,
    setActive,
    moveToNext,
    moveToPrevious,
  } = state

  const [dropdownOpen, setDropdownOpen] = useState(false)
  const committingRef = useRef(false)

  // Toast + Done-sound are tied to the user confirming the batch — the
  // button click (or the 'K' shortcut) calls commitAll. They fire only when
  // the batch actually contained at least one staged entry.
  const playDone = useDoneSound()
  const { t } = useTranslation()

  const commitAll = useCallback(async () => {
    if (committingRef.current) return
    committingRef.current = true
    const stagedCount = staged.size
    const clearsInbox = stagedCount > 0 && stagedCount === taskQueue.length
    try {
      const now = new Date()
      await db.transaction('rw', db.tasks, db.syncQueue, async () => {
        for (const [taskId, action] of staged) {
          const updates = buildTaskUpdate(action, now)
          await db.tasks.update(taskId, {
            ...updates,
            updatedAt: now,
            lastModified: now,
            synced: false,
          })
          await db.queueSyncChange('update', 'tasks', { localId: taskId })
        }
      })
      if (stagedCount > 0) {
        scheduleSync()
        playDone()
        showSuccessToast({
          message: clearsInbox
            ? t('quickNotes.clarify.completedToast')
            : t('quickNotes.clarify.completedToastPartial', { count: stagedCount }),
        })
      }
    } catch (error) {
      console.error('[useClarifyController] Commit failed:', error)
    } finally {
      committingRef.current = false
      onExit()
    }
  }, [staged, taskQueue.length, onExit, playDone, t])

  const finish = useCallback(() => {
    void commitAll()
  }, [commitAll])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (target) {
        const tag = target.tagName
        if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return

      switch (e.key) {
        case 'k':
        case 'K':
          if (dropdownOpen) return
          e.preventDefault()
          finish()
          return
        case 'Escape':
          e.preventDefault()
          onExit()
          return
        case 'ArrowUp':
          if (dropdownOpen) return
          e.preventDefault()
          moveToPrevious()
          return
        case 'ArrowDown':
          if (dropdownOpen) return
          e.preventDefault()
          moveToNext()
          return
      }

      if (dropdownOpen || !activeTaskId) return

      switch (e.key) {
        case 'h':
        case 'H':
          e.preventDefault()
          stageAction(activeTaskId, { kind: 'today' })
          return
        case 'm':
        case 'M':
          e.preventDefault()
          stageAction(activeTaskId, { kind: 'tomorrow' })
          return
        case 'r':
        case 'R':
          e.preventDefault()
          stageAction(activeTaskId, { kind: 'pause' })
          return
        case 'l':
        case 'L':
          e.preventDefault()
          stageAction(activeTaskId, { kind: 'delete' })
          return
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [activeTaskId, dropdownOpen, stageAction, moveToNext, moveToPrevious, finish, onExit])

  const handleStage = useCallback(
    (action: StagedAction) => {
      if (!activeTaskId) return
      stageAction(activeTaskId, action)
    },
    [activeTaskId, stageAction],
  )

  return {
    // Header-slot props
    headerProps: {
      done: staged.size,
      total: taskQueue.length,
      progress,
      onFinish: finish,
    },
    // Body-slot props
    bodyProps: {
      queue: taskQueue,
      activeTaskId,
      activeTask,
      staged,
      stackIds,
      onSelectActive: setActive,
      onStage: handleStage,
      onAnyDropdownOpenChange: setDropdownOpen,
    },
  }
}

